import {
  DEFAULT_TENANT_KEY,
  TENANT_DEFINITIONS,
  TENANT_KEYS,
  getTenantDefinition,
  type TenantDefinitionT,
  type TenantKeyT,
} from "@/app/config/tenant-registry";
import {
  getGeneralExternalRedirects,
  getRoutePath,
} from "@/app/config/navigation-ssot";
import { normalizeHost as normalizeHostValue } from "@/app/config/host-normalization";
import type {
  TenantLinkKeyT,
  TenantResolvedLinkT,
} from "@/app/config/tenant-link-types";

type CrossTenantLinkT = {
  title: string;
  tenantKey: TenantKeyT;
};

export type TenantConfigT = TenantDefinitionT & {
  hostnames: string[];
  websiteUrl: string;
  crossTenantLinks: CrossTenantLinkT[];
};

const LOCAL_TENANT_PORT = process.env.NEXT_PUBLIC_LOCAL_TENANT_PORT ?? "3000";
const IS_PRODUCTION = process.env.NODE_ENV === "production";
const GENERAL_EXTERNAL_REDIRECTS = getGeneralExternalRedirects();
const TENANT_WEBSITE_URL_ENV_VALUES: Record<TenantKeyT, string | undefined> = {
  "wiest-group": process.env.NEXT_PUBLIC_WIEST_WEBSITE_BASE_URL,
  "autohaus-rauch": process.env.NEXT_PUBLIC_RAUCH_WEBSITE_BASE_URL,
  "schuetz-gross-umstadt": process.env.NEXT_PUBLIC_SCHUETZ_WEBSITE_BASE_URL,
};
const TENANT_HOSTNAMES_ENV_VALUES: Record<TenantKeyT, string | undefined> = {
  "wiest-group": process.env.NEXT_PUBLIC_WIEST_HOSTNAMES,
  "autohaus-rauch": process.env.NEXT_PUBLIC_RAUCH_HOSTNAMES,
  "schuetz-gross-umstadt": process.env.NEXT_PUBLIC_SCHUETZ_HOSTNAMES,
};

export const normalizeHost = (host?: string | null): string => {
  return normalizeHostValue(host, {
    stripPort: true,
    stripWww: true,
  });
};

const parseHostnames = (value: string | undefined, fallback: string[]) => {
  const hostnames = (value ?? "")
    .split(",")
    .map((hostname) => normalizeHost(hostname))
    .filter(Boolean);

  if (hostnames.length > 0) {
    return hostnames;
  }

  return fallback.map((hostname) => normalizeHost(hostname)).filter(Boolean);
};

const hasExplicitProtocol = (value: string): boolean =>
  /^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(value);

const isLocalDevelopmentHostname = (hostname: string): boolean =>
  hostname === "localhost" ||
  hostname === "127.0.0.1" ||
  hostname.endsWith(".localhost");

const isAllowedWebsiteHostname = (hostname: string): boolean => {
  if (!hostname) {
    return false;
  }

  if (isLocalDevelopmentHostname(hostname)) {
    return !IS_PRODUCTION;
  }

  // Public tenant domains must be proper domain names.
  return hostname.includes(".");
};

const parseBaseUrl = (value: string | undefined, fallback: string): string => {
  const normalized = value?.trim();
  if (!normalized) {
    return fallback;
  }

  const hasProtocol = hasExplicitProtocol(normalized);
  const baseCandidates = hasProtocol
    ? [normalized]
    : [
        `https://${normalized}`,
        ...(!IS_PRODUCTION ? [`http://${normalized}`] : []),
      ];

  for (const candidate of baseCandidates) {
    try {
      const parsed = new URL(candidate);
      if (!parsed.hostname) {
        continue;
      }

      const isHttpProtocol =
        parsed.protocol === "http:" || parsed.protocol === "https:";
      if (!isHttpProtocol) {
        continue;
      }

      if (!isAllowedWebsiteHostname(parsed.hostname)) {
        continue;
      }

      const hasPort = parsed.port.length > 0;
      const shouldAddLocalPort =
        !IS_PRODUCTION && !hasPort && parsed.protocol.startsWith("http");
      const port = hasPort
        ? parsed.port
        : shouldAddLocalPort
          ? LOCAL_TENANT_PORT
          : "";
      const portSuffix = port ? `:${port}` : "";
      const path = parsed.pathname.replace(/\/+$/, "") || "/";

      return `${parsed.protocol}//${parsed.hostname}${portSuffix}${path}`;
    } catch {
      continue;
    }
  }

  return fallback;
};

const buildTenantConfig = (tenantKey: TenantKeyT): TenantConfigT => {
  const definition = getTenantDefinition(tenantKey);

  return {
    ...definition,
    hostnames: parseHostnames(
      TENANT_HOSTNAMES_ENV_VALUES[definition.key],
      definition.hostnameFallbacks
    ),
    websiteUrl: parseBaseUrl(
      TENANT_WEBSITE_URL_ENV_VALUES[definition.key],
      definition.websiteUrlFallback
    ),
    crossTenantLinks: definition.crossTenantKeys.map((crossTenantKey) => ({
      title: TENANT_DEFINITIONS[crossTenantKey].displayName,
      tenantKey: crossTenantKey,
    })),
  };
};

const TENANT_CONFIGS = TENANT_KEYS.map((tenantKey) =>
  buildTenantConfig(tenantKey)
);

const TENANT_CONFIGS_BY_KEY = TENANT_CONFIGS.reduce(
  (acc, tenant) => {
    acc[tenant.key] = tenant;
    return acc;
  },
  {} as Record<TenantKeyT, TenantConfigT>
);

const DEFAULT_TENANT_CONFIG =
  TENANT_CONFIGS_BY_KEY[DEFAULT_TENANT_KEY] ?? TENANT_CONFIGS[0];

const findTenantConfigByHost = (
  hostname?: string | null
): TenantConfigT | null => {
  const normalizedHost = normalizeHost(hostname);
  if (!normalizedHost) {
    return null;
  }

  return (
    TENANT_CONFIGS.find((tenant) =>
      tenant.hostnames.includes(normalizedHost)
    ) ?? null
  );
};

export const getTenantConfig = (tenantKey: TenantKeyT): TenantConfigT =>
  TENANT_CONFIGS_BY_KEY[tenantKey];

export const getTenantWebsiteUrl = (tenantKey: TenantKeyT): string =>
  getTenantConfig(tenantKey).websiteUrl;

export const getTenantConfigByHost = (
  hostname?: string | null
): TenantConfigT => {
  return findTenantConfigByHost(hostname) ?? DEFAULT_TENANT_CONFIG;
};

export const isTenantHost = (
  tenantKey: TenantKeyT,
  hostname?: string | null
): boolean =>
  getTenantConfig(tenantKey).hostnames.includes(normalizeHost(hostname));

export const resolveTenantLink = (
  tenantKey: TenantKeyT,
  linkKey: TenantLinkKeyT
): TenantResolvedLinkT => getTenantConfig(tenantKey).tenantLinks[linkKey];

// Relative path of the tenant's appointment page (online booking or phone info).
export const getTenantAppointmentHref = (tenantKey: TenantKeyT): string =>
  resolveTenantLink(tenantKey, "appointment").href;

export const hasOnlineAppointmentBooking = (tenantKey: TenantKeyT): boolean =>
  getTenantAppointmentHref(tenantKey) === getRoutePath("online-appointments");

export const resolveExternalRedirect = (args: {
  host?: string | null;
  pathname: string;
}): string | null => {
  const { host, pathname } = args;
  const generalRedirect = GENERAL_EXTERNAL_REDIRECTS[pathname];
  if (generalRedirect) {
    return generalRedirect;
  }

  const tenantConfig = findTenantConfigByHost(host);
  if (!tenantConfig) {
    return null;
  }

  return tenantConfig.externalRedirects[pathname] ?? null;
};
